import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Skeleton,
    Typography,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Store as StoreIcon,
    VideoCall as VideoCallIcon,
} from '@mui/icons-material';

import { fetchStores } from '../../actions/storeActions';
import { fetchStoreCatalog } from '../../actions/catalogActions';
import { useStoreHours } from '../../hooks/useStoreHours';
import ProductCard from './ProductCard';
import CartButton from '../cart/CartButton';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    FONTS_HREF,
    callButtonSx,
    secondaryButtonSx,
    skeletonSx,
} from './storefrontStyles';

export const callRequestPath = (storeId) => `/${storeId}/create-call-request`;

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
        md: 'repeat(4, 1fr)',
    },
    gap: { xs: 1.5, md: 2 },
};

const ProductGridSkeleton = ({ count = 8 }) => (
    <Box sx={gridSx}>
        {Array.from({ length: count }).map((_, i) => (
            <Box key={i}>
                <Skeleton
                    variant="rectangular"
                    sx={{
                        ...skeletonSx,
                        width: '100%',
                        height: 0,
                        pt: '100%',
                        borderRadius: '8px',
                    }}
                />
                <Skeleton sx={{ ...skeletonSx, mt: 1 }} width="80%" />
                <Skeleton sx={skeletonSx} width="40%" />
            </Box>
        ))}
    </Box>
);

const StoreInfo = ({ store, isLoading }) => {
    const isOpen = useStoreHours(store);

    if (!store) {
        return isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton
                    variant="circular"
                    width={72}
                    height={72}
                    sx={skeletonSx}
                />
                <Box sx={{ flex: 1 }}>
                    <Skeleton sx={skeletonSx} width="50%" height={36} />
                    <Skeleton sx={skeletonSx} width="30%" />
                </Box>
            </Box>
        ) : null;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
                src={store.profileImageUrl || undefined}
                alt={store.name}
                sx={{
                    width: { xs: 64, md: 80 },
                    height: { xs: 64, md: 80 },
                    backgroundColor: COLORS.card,
                    border: `1px solid ${COLORS.border}`,
                }}
            >
                <StoreIcon sx={{ color: '#555' }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_TITLE,
                        fontWeight: 800,
                        fontSize: { xs: '1.8rem', md: '2.4rem' },
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        lineHeight: 1,
                        color: COLORS.text,
                    }}
                >
                    {store.name}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        mt: 0.75,
                        flexWrap: 'wrap',
                    }}
                >
                    {store.categoryName && (
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: COLORS.muted,
                                fontFamily: FONT_BODY,
                            }}
                        >
                            {store.categoryName}
                        </Typography>
                    )}
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 500,
                            borderRadius: '3px',
                            padding: '1px 6px',
                            backgroundColor: isOpen
                                ? 'rgba(41,210,171,0.15)'
                                : 'rgba(226,75,74,0.15)',
                            color: isOpen ? COLORS.open : COLORS.closed,
                        }}
                    >
                        {isOpen ? 'Abierto' : 'Cerrado'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const StorePage = ({
    store,
    isFetchingStores,
    catalog,
    fetchStores,
    fetchStoreCatalog,
}) => {
    const { storeId } = useParams();
    const navigate = useNavigate();
    const callPath = callRequestPath(storeId);
    const storeHasNoProducts = !!store && store.productCount === 0;

    useEffect(() => {
        if (!store) {
            fetchStores();
        }
    }, [store, fetchStores]);

    useEffect(() => {
        if (!storeHasNoProducts) {
            fetchStoreCatalog(storeId, 0);
        }
    }, [storeId, storeHasNoProducts, fetchStoreCatalog]);

    // Tienda sin catálogo -> comportamiento anterior: directo a llamar.
    const catalogIsEmpty =
        !!catalog &&
        catalog.loaded &&
        !catalog.isFetching &&
        !catalog.failed &&
        catalog.items.length === 0;

    useEffect(() => {
        if (storeHasNoProducts || catalogIsEmpty) {
            navigate(callPath, { replace: true });
        }
    }, [storeHasNoProducts, catalogIsEmpty, callPath, navigate]);

    const items = (catalog && catalog.items) || [];
    const isFirstLoad =
        items.length === 0 &&
        (!catalog || !catalog.loaded || catalog.isFetching);
    const failed = !!catalog && catalog.failed && items.length === 0;

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: COLORS.background,
                color: COLORS.text,
            }}
        >
            <link href={FONTS_HREF} rel="stylesheet" />

            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    px: { xs: 2, md: 4 },
                    py: { xs: 2, md: 4 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Button
                        component={RouterLink}
                        to="/home"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            color: COLORS.muted,
                            '&:hover': { color: 'white' },
                        }}
                    >
                        Inicio
                    </Button>
                    <CartButton />
                </Box>

                {/* Header de la tienda */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: 'space-between',
                        gap: 2.5,
                        p: { xs: 2, md: 3 },
                        mb: 2,
                        borderRadius: '12px',
                        backgroundColor: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <StoreInfo
                        store={store}
                        isLoading={!store && isFetchingStores}
                    />
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<VideoCallIcon />}
                        onClick={() => navigate(callPath)}
                        sx={{ ...callButtonSx, flexShrink: 0 }}
                    >
                        Llamar a la tienda
                    </Button>
                </Box>

                {store && store.description && (
                    <Typography
                        sx={{
                            color: COLORS.muted,
                            fontFamily: FONT_BODY,
                            fontSize: '0.95rem',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-line',
                            mb: 3,
                            px: { xs: 0.5, md: 1 },
                        }}
                    >
                        {store.description}
                    </Typography>
                )}

                <Typography
                    sx={{
                        fontFamily: FONT_TITLE,
                        fontWeight: 700,
                        fontSize: { xs: '1rem', md: '1.15rem' },
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        mb: 2,
                        mt: 1,
                    }}
                >
                    Productos
                </Typography>

                {isFirstLoad ? (
                    <ProductGridSkeleton />
                ) : failed ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography
                            sx={{
                                color: COLORS.muted,
                                fontFamily: FONT_BODY,
                                mb: 2,
                            }}
                        >
                            No pudimos cargar los productos. Igual podés llamar
                            a la tienda y consultar lo que necesites.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => fetchStoreCatalog(storeId, 0)}
                            sx={secondaryButtonSx}
                        >
                            Reintentar
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Box sx={gridSx}>
                            {items.map((product) => (
                                <ProductCard
                                    key={product.productId}
                                    product={product}
                                />
                            ))}
                        </Box>
                        {catalog.hasMore && catalog.nextOffset !== null && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 4,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    disabled={catalog.isFetchingMore}
                                    onClick={() =>
                                        fetchStoreCatalog(
                                            storeId,
                                            catalog.nextOffset
                                        )
                                    }
                                    sx={{ ...secondaryButtonSx, px: 4 }}
                                >
                                    {catalog.isFetchingMore ? (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: 'white' }}
                                        />
                                    ) : (
                                        'Ver más'
                                    )}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        store: state.stores.stores[ownProps.storeId],
        isFetchingStores: state.stores.isFetching,
        catalog: state.catalog.byStore[ownProps.storeId],
    };
};

const ConnectedStorePage = connect(mapStateToProps, {
    fetchStores,
    fetchStoreCatalog,
})(StorePage);

// storeId de la URL como prop, igual que CreateCallRequestWrapper en App.js
const StorePageWrapper = () => {
    const { storeId } = useParams();
    return <ConnectedStorePage storeId={storeId} />;
};

export default StorePageWrapper;
